import React, { useState, useMemo } from "react";
import { Search, Phone, Mail, MapPin, Loader2, Trash2, FileText, CalendarPlus, X, Thermometer, Flame, Send, BellOff, Bell } from "lucide-react";
import { toast } from "sonner";
import {
  updateBooking, deleteBooking, sendLeadFollowup, logLeadResponse,
  STATUS_OPTIONS, LEAD_SOURCE_OPTIONS, OUTCOME_OPTIONS,
} from "@/lib/dashboardApi";
import { buildEvent, googleCalendarUrl, downloadIcs, defaultWhen, dateOnly } from "@/lib/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ORANGE = "#F97316";
const SALES_ROLES = ["Owner", "Office", "Sales"];
const FIELD_ROLES = ["Lead Tech", "Technician", "Installer", "Apprentice"];

const isWeatherLead = (b) =>
  (b.tags || []).includes("WEATHER_LEAD") ||
  b.lead_source === "Weather Intelligence" ||
  !!(b.weather_context && Object.keys(b.weather_context).length);
const isHeatLead = (b) => {
  const wx = b.weather_context || {};
  const temp = wx.temp != null ? Number(wx.temp) : null;
  const hi = wx.heat_index != null ? Number(wx.heat_index) : null;
  return (temp != null && temp >= 88) || (hi != null && hi >= 90) ||
    /heat|hot/i.test(wx.trigger || "") || (b.tags || []).some((t) => /heat|hot/i.test(t));
};
const SCORE_STYLE = {
  URGENT: "bg-red-100 text-red-700 border-red-200",
  HIGH: "bg-orange-100 text-orange-700 border-orange-200",
  MEDIUM: "bg-amber-100 text-amber-700 border-amber-200",
  LOW: "bg-sky-100 text-sky-700 border-sky-200",
};
const WeatherBadge = ({ b }) => {
  const wx = b.weather_context || {};
  const score = (b.lead_score || "").toUpperCase();
  const temp = wx.temp != null ? Math.round(Number(wx.temp)) : null;
  const heat = isHeatLead(b);
  const cls = SCORE_STYLE[score] || "bg-sky-100 text-sky-700 border-sky-200";
  return (
    <span data-testid={`weather-badge-${b.id}`}
      className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>
      {heat ? <Flame className="w-3 h-3" /> : <Thermometer className="w-3 h-3" />}
      {heat ? "Heat Lead" : "Weather"}{temp != null ? ` · ${temp}°` : ""}{score ? ` · ${score}` : ""}
    </span>
  );
};

const RequestsTable = ({ bookings, team, onRefresh, onCreateInvoice }) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [weatherOnly, setWeatherOnly] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [calTarget, setCalTarget] = useState(null);
  const [calWhen, setCalWhen] = useState("");
  const [calDur, setCalDur] = useState(60);

  const openCalendar = (b) => { setCalTarget(b); setCalWhen(defaultWhen(b)); setCalDur(60); };
  const addToCalendar = (kind) => {
    if (!calTarget || !calWhen) return;
    const ev = buildEvent(calTarget, calWhen, Number(calDur) || 60);
    if (kind === "google") window.open(googleCalendarUrl(ev), "_blank", "noopener");
    else downloadIcs(ev, `launch-${(calTarget.name || "appointment").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.ics`);
    const d = dateOnly(calWhen);
    if (d && d !== (calTarget.scheduled_date || "").slice(0, 10)) {
      updateBooking(calTarget.id, { scheduled_date: d }).then(onRefresh).catch(() => {});
    }
    toast.success(kind === "google" ? "Opening Google Calendar…" : "Calendar file downloaded");
    setCalTarget(null);
  };

  const salesReps = (team || []).filter((t) => SALES_ROLES.includes(t.role));
  const installers = (team || []).filter((t) => FIELD_ROLES.includes(t.role));

  const filtered = useMemo(() => (bookings || []).filter((b) => {
    const matchesQuery = !query || [b.name, b.phone, b.email, b.address, b.service].join(" ").toLowerCase().includes(query.toLowerCase());
    const matchesStatus = statusFilter === "all" || (b.status || "new") === statusFilter;
    const matchesWeather = !weatherOnly || isWeatherLead(b);
    return matchesQuery && matchesStatus && matchesWeather;
  }), [bookings, query, statusFilter, weatherOnly]);
  const weatherCount = useMemo(() => (bookings || []).filter(isWeatherLead).length, [bookings]);

  const handleUpdate = async (id, field, value) => {
    setSavingId(id);
    try { await updateBooking(id, { [field]: field === "job_value" ? parseFloat(value) || 0 : value }); toast.success("Updated"); onRefresh(); }
    catch { toast.error("Failed to update"); }
    finally { setSavingId(null); }
  };
  const handleFollowup = async (b) => {
    setSavingId(b.id);
    try {
      const r = await sendLeadFollowup(b.id);
      toast.success(r.sms_sent ? "Follow-up sent (email + text)" : r.email_sent ? "Follow-up email sent" : "Follow-up logged (no email/text on file)");
      onRefresh();
    } catch { toast.error("Could not send follow-up"); }
    finally { setSavingId(null); }
  };
  const toggleContact = async (b) => {
    const stopping = !b.follow_up_stopped;
    setSavingId(b.id);
    try {
      await updateBooking(b.id, { do_not_contact: stopping });
      toast.success(stopping ? "Automated follow-up stopped" : "Automated follow-up resumed");
      onRefresh();
    } catch { toast.error("Failed to update"); }
    finally { setSavingId(null); }
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await deleteBooking(deleteTarget.id); toast.success("Request deleted"); setDeleteTarget(null); onRefresh(); }
    catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const inputCls = "bg-white border border-slate-300 rounded-md px-2 py-1.5 text-slate-800 text-xs focus:outline-none focus:border-[#F97316]";
  return (
    <div className="space-y-4" data-testid="requests-table">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, address, service..." data-testid="requests-search"
            className="w-full bg-white border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/15" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="requests-status-filter"
          className="bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-[#F97316]">
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
        </select>
        <button onClick={() => setWeatherOnly((v) => !v)} data-testid="weather-filter-toggle" title="Show only weather-triggered leads"
          className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 border font-semibold text-sm transition-colors ${weatherOnly ? "bg-orange-500 border-orange-500 text-white shadow-sm" : "bg-white border-slate-300 text-slate-700 hover:border-orange-400 hover:text-orange-600"}`}>
          <Flame className="w-4 h-4" /> Weather Leads
          {weatherCount > 0 && <span data-testid="weather-lead-count" className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[11px] leading-none ${weatherOnly ? "bg-white/25 text-white" : "bg-orange-100 text-orange-700"}`}>{weatherCount}</span>}
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Customer</th>
                <th className="text-left font-semibold px-4 py-3">Service</th>
                <th className="text-left font-semibold px-4 py-3">Property Address</th>
                <th className="text-left font-semibold px-4 py-3">Lead Source</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-left font-semibold px-4 py-3">Outcome</th>
                <th className="text-left font-semibold px-4 py-3">Sales Rep</th>
                <th className="text-left font-semibold px-4 py-3">Installer</th>
                <th className="text-left font-semibold px-4 py-3">Sold $</th>
                <th className="text-left font-semibold px-4 py-3">Follow-Up</th>
                <th className="text-left font-semibold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-slate-400 py-10">No requests found</td></tr>
              ) : filtered.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50 align-top" data-testid={`request-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-slate-900 font-medium">{b.name}</p>
                    <a href={`tel:${b.phone}`} className="flex items-center gap-1 text-slate-500 text-xs mt-0.5 hover:text-[#F97316]"><Phone className="w-3 h-3" />{b.phone}</a>
                    {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-1 text-slate-500 text-xs mt-0.5 hover:text-[#F97316]"><Mail className="w-3 h-3" />{b.email}</a>}
                    {b.message && <p className="text-slate-400 text-xs mt-1 max-w-[200px] whitespace-normal">{b.message}</p>}
                    {isWeatherLead(b) && <div><WeatherBadge b={b} /></div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{b.service}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {b.address ? (<span className="flex items-start gap-1 whitespace-normal max-w-[160px]"><MapPin className="w-3.5 h-3.5 text-[#F97316] mt-0.5 shrink-0" />{b.address}</span>) : <span className="text-slate-300">&mdash;</span>}
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.lead_source || ""} onChange={(e) => handleUpdate(b.id, "lead_source", e.target.value)} disabled={savingId === b.id} className={inputCls}>
                      {LEAD_SOURCE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.status || "new"} onChange={(e) => handleUpdate(b.id, "status", e.target.value)} disabled={savingId === b.id} className={inputCls} data-testid={`request-status-${b.id}`}>
                      {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.outcome || "pending"} onChange={(e) => handleUpdate(b.id, "outcome", e.target.value)} disabled={savingId === b.id} className={inputCls}>
                      {OUTCOME_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.assigned_to || ""} onChange={(e) => handleUpdate(b.id, "assigned_to", e.target.value)} disabled={savingId === b.id} className={`${inputCls} max-w-[130px]`}>
                      <option value="">Unassigned</option>
                      {salesReps.map((t) => (<option key={t.id || t.name} value={t.name}>{t.name}</option>))}
                    </select>
                    <label className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-600 cursor-pointer" title="Self-generated lead adds +2% commission">
                      <input type="checkbox" checked={!!b.self_generated} onChange={(e) => handleUpdate(b.id, "self_generated", e.target.checked)} disabled={savingId === b.id} className="w-4 h-4 accent-[#F97316]" data-testid={`self-gen-${b.id}`} />
                      Self-gen <span className="text-[#F97316] font-semibold">+2%</span>
                    </label>
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.installer || ""} onChange={(e) => handleUpdate(b.id, "installer", e.target.value)} disabled={savingId === b.id} className={`${inputCls} max-w-[130px]`}>
                      <option value="">Unassigned</option>
                      {installers.map((t) => (<option key={t.id || t.name} value={t.name}>{t.name}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input type="number" defaultValue={b.job_value || ""} placeholder="0"
                      onBlur={(e) => { if ((parseFloat(e.target.value) || 0) !== (b.job_value || 0)) handleUpdate(b.id, "job_value", e.target.value); }}
                      className={`${inputCls} w-24`} />
                  </td>
                  <td className="px-4 py-3">
                    {b.follow_up_stopped ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5" title={`Stopped: ${b.follow_up_stop_reason || "manual"}`}>
                        <BellOff className="w-3 h-3" /> {(b.follow_up_stop_reason || "stopped").replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-500" data-testid={`followup-next-${b.id}`}>
                        {b.follow_up_count ? `${b.follow_up_count} sent · ` : ""}
                        {b.next_follow_up_at ? `next ${new Date(b.next_follow_up_at).toLocaleDateString()}` : "scheduled"}
                      </span>
                    )}
                    <div className="flex items-center gap-1 mt-1.5">
                      <button onClick={() => handleFollowup(b)} disabled={savingId === b.id} data-testid={`followup-now-${b.id}`}
                        title="Send a follow-up now (email + text if configured)"
                        className="inline-flex items-center gap-1 text-[#F97316] hover:bg-[#F97316]/10 px-1.5 py-1 rounded text-[11px] font-semibold disabled:opacity-50">
                        <Send className="w-3 h-3" /> Now
                      </button>
                      <button onClick={() => toggleContact(b)} disabled={savingId === b.id} data-testid={`followup-toggle-${b.id}`}
                        title={b.follow_up_stopped ? "Resume automated follow-up" : "Stop automated follow-up (do not contact)"}
                        className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 px-1.5 py-1 rounded text-[11px] font-semibold disabled:opacity-50">
                        {b.follow_up_stopped ? <><Bell className="w-3 h-3" /> Resume</> : <><BellOff className="w-3 h-3" /> Stop</>}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openCalendar(b)} title="Add to calendar" data-testid={`add-calendar-${b.id}`}
                        className="flex items-center gap-1 text-[#2563eb] hover:bg-[#2563eb]/10 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap">
                        <CalendarPlus className="w-3.5 h-3.5" /> Calendar
                      </button>
                      {b.status === "completed" && onCreateInvoice && (
                        <button onClick={() => onCreateInvoice(b)} title="Create invoice from this job" data-testid={`create-invoice-${b.id}`}
                          className="flex items-center gap-1 text-[#F97316] hover:bg-[#F97316]/10 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap">
                          <FileText className="w-3.5 h-3.5" /> Invoice
                        </button>
                      )}
                      <button onClick={() => setDeleteTarget(b)} title="Delete request" className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {savingId && <p className="text-slate-400 text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Saving...</p>}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the request from <strong>{deleteTarget?.name}</strong> ({deleteTarget?.service}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {calTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="calendar-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-slate-900 font-semibold text-lg flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#2563eb]" /> Add to Calendar</h3>
              <button onClick={() => setCalTarget(null)} data-testid="close-calendar-modal" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="text-slate-900 font-semibold">{calTarget.name}</p>
                <p className="text-slate-500">{calTarget.service}{calTarget.address ? ` · ${calTarget.address}` : ""}</p>
                <p className="text-slate-500">{calTarget.phone}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Date & time</label>
                  <input type="datetime-local" value={calWhen} onChange={(e) => setCalWhen(e.target.value)} data-testid="calendar-when"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#2563eb]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Duration</label>
                  <select value={calDur} onChange={(e) => setCalDur(Number(e.target.value))} data-testid="calendar-duration"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#2563eb]">
                    <option value={30}>30 min</option><option value={60}>1 hour</option><option value={90}>1.5 hours</option><option value={120}>2 hours</option><option value={180}>3 hours</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <button onClick={() => addToCalendar("google")} data-testid="calendar-google"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#ea6a0c] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-colors">
                  <CalendarPlus className="w-4 h-4" /> Google Calendar
                </button>
                <button onClick={() => addToCalendar("ics")} data-testid="calendar-ics"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#2563eb] hover:text-[#2563eb] text-slate-700 font-semibold px-4 py-2.5 rounded-lg text-sm transition-colors">
                  <CalendarPlus className="w-4 h-4" /> Download .ics (Apple/Outlook)
                </button>
              </div>
              <p className="text-slate-400 text-xs">Google opens a pre-filled event to save. The .ics file imports into Apple Calendar or Outlook. Saving also sets this request's scheduled date.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsTable;
